import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X, Loader2, Eye, EyeOff, Server, Globe, Key, Shield } from 'lucide-react';

const BucketForm = ({ bucket, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: bucket?.name || '',
    endpoint: bucket?.endpoint || '',
    accessKeyId: bucket?.accessKeyId || '',
    secretAccessKey: bucket?.secretAccessKey || '',
    bucket: bucket?.bucket || '',
    publicUrl: bucket?.publicUrl || '',
  });
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.endpoint || !form.accessKeyId || !form.secretAccessKey || !form.bucket || !form.publicUrl) {
      toast.error('All fields are required');
      return;
    }
    setSaving(true);
    try {
      let res;
      if (bucket) {
        res = await axios.put(`/api/buckets/${bucket.id}`, form);
      } else {
        res = await axios.post('/api/buckets', form);
      }
      toast.success(bucket ? 'Bucket updated' : 'Bucket added');
      onSave(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save bucket');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" />

      {/* Modal */}
      <div className="relative bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl animate-scale-in-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="font-heading font-bold text-foreground">{bucket ? 'Edit Bucket' : 'Add Bucket'}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Configure your R2 storage credentials</p>
          </div>
          <button onClick={onCancel} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors cursor-pointer" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Bucket Name */}
          <div>
            <label className="label">
              <Server size={12} className="inline mr-1" />
              Bucket Name
            </label>
            <input name="name" value={form.name} onChange={handleChange} placeholder="My Photos Bucket" className="input" />
          </div>

          {/* Endpoint */}
          <div>
            <label className="label">
              <Globe size={12} className="inline mr-1" />
              Endpoint URL
            </label>
            <input name="endpoint" value={form.endpoint} onChange={handleChange} placeholder="https://<account-id>.r2.cloudflarestorage.com" className="input" />
          </div>

          {/* Credentials */}
          <div className="p-4 bg-muted/50 rounded-xl space-y-4 border border-border/50">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Credentials</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">
                  <Key size={12} className="inline mr-1" />
                  Access Key ID
                </label>
                <input name="accessKeyId" value={form.accessKeyId} onChange={handleChange} className="input" />
              </div>
              <div>
                <label className="label">
                  <Shield size={12} className="inline mr-1" />
                  Secret Key
                </label>
                <div className="relative">
                  <input name="secretAccessKey" type={showSecret ? 'text' : 'password'} value={form.secretAccessKey} onChange={handleChange} className="input pr-10" />
                  <button type="button" onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground rounded transition-colors cursor-pointer"
                    aria-label={showSecret ? 'Hide secret' : 'Show secret'}>
                    {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Bucket Details */}
          <div>
            <label className="label">R2 Bucket Name</label>
            <input name="bucket" value={form.bucket} onChange={handleChange} placeholder="my-photos-bucket" className="input" />
          </div>

          {/* Public URL */}
          <div>
            <label className="label">Public URL</label>
            <input name="publicUrl" value={form.publicUrl} onChange={handleChange} placeholder="https://pub-xxx.r2.dev" className="input" />
            <p className="text-xs text-muted-foreground mt-1.5">Public bucket URL or custom domain for serving images</p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onCancel} className="btn-outline">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {bucket ? 'Update Bucket' : 'Add Bucket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BucketForm;
